import { useTranslation } from 'react-i18next'
import { OdinInput } from '@/components/ui/odin/form/odin-input'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { requiredEmail } from 'utils/validations'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { useState } from 'react'
import { showSuccessToast } from 'utils/toast'
import { useSelector } from 'react-redux'
import { messagesSelectors } from 'store/messages'
import { exportChat } from 'api/chats'
import { settingsSelectors } from 'store/settings'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useModal } from 'providers/ModalProvider'

type ExportChatModalProps = {
  onSuccess?: () => void
}

const schema = z.object({
  email: requiredEmail,
})

type ExportChatFormValues = z.infer<typeof schema>

export const ExportChatModal = ({ onSuccess }: ExportChatModalProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const chatId = useSelector(messagesSelectors.chatId)
  const currentUser = useSelector(settingsSelectors.userDetails)
  const { closeModal } = useModal()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ExportChatFormValues>({
    resetOptions: { keepDirtyValues: true },
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    // Prefill from user details; RHF `values` keeps it in sync when the data arrives.
    values: { email: currentUser?.email || '' },
  })

  const onSubmit = async (values: ExportChatFormValues) => {
    if (!chatId) {
      return
    }
    try {
      setLoading(true)
      await exportChat(chatId, values.email)
      onSuccess?.()
      showSuccessToast(t('chatExportedSuccess'))
      closeModal()
    } catch (e) {
      console.error({ e })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal onClose={closeModal}>
      <ModalHeader title={t('exportChat')} icon="envelope" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className={'space-y-1'}>
          <OdinInput type={'email'} label={t('email')} error={errors.email?.message} {...register('email')} />
          <div className={'h-2'} />
        </ModalBody>
        <ModalFooter>
          <OdinButton type={'submit'} className={'w-full'} loading={loading}>
            <span className={'text-sm'}>{t('exportChat')}</span>
          </OdinButton>
        </ModalFooter>
      </form>
    </Modal>
  )
}
